import { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';

const FormContext = createContext();

export const useFormContext = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context;
};

export const FormProvider = ({ children }) => {
  const [forms, setForms] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const appToken = useSelector((state) => state.appToken?.apptoken || '');
  const userData = useSelector((state) => state.loginData?.loginData || {});

  // Debug: FormContext token durumunu logla
  console.log('FormContext - Token Debug:', {
    appToken: appToken,
    userData: userData,
    userDataId: userData?.id
  });

  const apiHeaders = {
    headers: {
      ApplicationToken: appToken,
      User: userData?.id || '',
    },
  };

  // Belirli bir klasördeki formları getir
  const fetchFormsByFolder = useCallback(async (folderId = 0) => {
    setLoading(true);
    try {
      const response = await axios.get(`https://app.hhoms.com/api/form/Libraries/list/${folderId}`, apiHeaders);
      const formData = response.data.formLibraries || response.data || [];
      setForms(formData);
      setError(null);
      return formData;
    } catch (error) {
      console.error('Formlar alınırken hata oluştu:', error);
      setError('Formlar yüklenirken bir hata oluştu. Lütfen tekrar deneyin.');
      return [];
    } finally {
      setLoading(false);
    }
  }, [appToken, userData?.id]);

  const generateFormLink = useCallback((formId) => `${window.location.origin}/form/${formId}`, []);

  const getForm = useCallback((formId) => forms.find(form => form.id === formId), [forms]);

  const updateForm = useCallback(async (formData) => {
    setLoading(true);
    try {
      const response = await axios.put(
        `https://app.hhoms.com/api/form/Libraries/${formData.id}`, 
        formData, 
        apiHeaders
      );
      setForms(prevForms => prevForms.map(form => (form.id === formData.id ? response.data : form)));
      setError(null);
      return response.data;
    } catch (error) {
      console.error('Form güncellenirken hata oluştu:', error);
      setError('Form güncellenirken bir hata oluştu. Lütfen tekrar deneyin.');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [appToken, userData?.id]);

  const deleteForm = useCallback(async (formId) => {
    if (!window.confirm('Bu formu silmek istediğinizden emin misiniz?')) {
      return false;
    }

    setLoading(true);
    try {
      await axios.delete(`https://app.hhoms.com/api/form/Libraries/${formId}`, apiHeaders);
      setForms(prevForms => prevForms.filter(form => form.id !== formId));
      setError(null);
      return true;
    } catch (error) {
      console.error('Form silinirken hata oluştu:', error);
      setError('Form silinirken bir hata oluştu. Lütfen tekrar deneyin.');
      return false;
    } finally {
      setLoading(false);
    }
  }, [appToken, userData?.id]);

  const publishForm = useCallback(async (formId) => {
    setLoading(true);
    try {
      const formLink = generateFormLink(formId);
      setForms(prevForms => prevForms.map(form => 
        form.id === formId 
          ? { ...form, isPublished: true, publishedAt: new Date().toISOString(), formLink } 
          : form
      ));
      setError(null);
      return formLink;
    } catch (error) {
      console.error('Form yayınlanırken hata oluştu:', error);
      setError('Form yayınlanırken bir hata oluştu. Lütfen tekrar deneyin.');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [generateFormLink]);

  const value = {
    forms,
    loading,
    error,
    fetchFormsByFolder,
    getForm,
    updateForm,
    deleteForm,
    publishForm,
    generateFormLink
  };

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
};
